import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { formatNewsMonthYear } from "@/lib/displayOrderDates";

type NewsItem = {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  slug: string | null;
  published_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export function NewsListPage() {
  const [items, setItems] = useState<NewsItem[]>([]);

  useEffect(() => {
    void supabase
      .from("news")
      .select("id,title,description,image_url,slug,published_at,created_at,updated_at")
      .is("branch_id", null)
      .is("batch_id", null)
      .is("association_id", null)
      .order("updated_at", { ascending: false })
      .then(({ data }) => setItems((data as NewsItem[]) ?? []));
  }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 py-14">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-brand-maroon">
        News
      </p>
      <h1 className="mt-2 font-display text-4xl font-bold text-brand-blue">
        OBA News
      </h1>
      <p className="mt-4 text-brand-blue/85">
        Official updates published by the Old Boys’ Association.
      </p>
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {items.length === 0 && (
          <p className="text-sm text-brand-blue/60 col-span-full">
            No news published yet.
          </p>
        )}
        {items.map((n) => {
          const dateLine = formatNewsMonthYear(n.updated_at ?? n.created_at);
          return (
          <article
            key={n.id}
            className="overflow-hidden rounded-2xl border border-brand-gray bg-white shadow-sm"
          >
            <div className="aspect-[16/9] bg-brand-gray">
              {n.image_url ? (
                <img
                  src={n.image_url}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full items-center justify-center text-xs text-brand-blue/50">
                  No image
                </div>
              )}
            </div>
            <div className="p-6">
              {dateLine ? (
                <time
                  dateTime={n.updated_at ?? n.created_at ?? undefined}
                  className="text-xs font-semibold uppercase tracking-wide text-brand-maroon"
                >
                  {dateLine}
                </time>
              ) : null}
              <Link
                to={n.slug ? `/news/${n.slug}` : "/news"}
                className="mt-2 block font-display text-2xl font-bold text-brand-blue hover:underline"
              >
                {n.title}
              </Link>
              {n.description ? (
                <p className="mt-3 text-brand-blue/85 whitespace-pre-line line-clamp-4">
                  {n.description}
                </p>
              ) : null}
              {n.slug ? (
                <Link
                  to={`/news/${n.slug}`}
                  className="mt-4 inline-block text-xs font-bold uppercase tracking-wide text-brand-maroon hover:underline"
                >
                  Read more →
                </Link>
              ) : null}
            </div>
          </article>
        );
        })}
      </div>
    </div>
  );
}
